import {
  takeLatest, takeEvery, put, call, all,
} from 'redux-saga/effects';
import Service from '@services/fhq-ip-whitelist-record';
import * as actions from '@actions';
import { IdType } from '@rootCommon/types/common';
import { FHQStatus, IpWhitelistRecord, IpWhitelistRecordUpdateDto } from '@rootCommon/types/fhq';

import { AxiosError } from 'axios';
import { SagaGenerator } from './index';

// fetch
function* fetchFhqIpWhitelistRecordsAsync(action: { payload: IdType }) {
  try {
    const records: IpWhitelistRecord[] = yield call(Service.getListBySiteId, action.payload);

    yield put(actions.fetchIpWhitelistRecords.success(records));
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.fetchIpWhitelistRecords.failure(error));
  }
}

function* watchFetchIpWhitelistRecords() {
  yield takeLatest(actions.fetchIpWhitelistRecords.request, fetchFhqIpWhitelistRecordsAsync);
}

// change
function* changeFhqIpWhitelistRecordAsync(action: {
  payload: { siteId: IdType; data: IpWhitelistRecord[] | IpWhitelistRecordUpdateDto[] };
}) {
  try {
    // type guard
    const isOldData = (
      record: IpWhitelistRecord | IpWhitelistRecordUpdateDto,
    ): record is IpWhitelistRecord => 'id' in record;

    const { siteId } = action.payload;
    const records = action.payload.data;

    const recordsNonChanged: IpWhitelistRecord[] = [];
    const recordsForUpdate: IpWhitelistRecord[] = [];
    const recordsForDelete: IpWhitelistRecord[] = [];
    const recordsForCreate: IpWhitelistRecordUpdateDto[] = [];

    // map data
    records?.forEach((record) => {
      if (isOldData(record)) {
        record.status === undefined && recordsNonChanged.push(record);
        record.status === FHQStatus.UPDATE && recordsForUpdate.push(record);
        record.status === FHQStatus.DELETE && recordsForDelete.push(record);
      } else {
        record.status === FHQStatus.CREATE && recordsForCreate.push(record);
      }
    });

    const created: IpWhitelistRecord[] = yield all(
      recordsForCreate.map((record) => call(Service.create, { siteId, ...record })),
    );
    const updated: IpWhitelistRecord[] = yield all(
      recordsForUpdate.map((record) => call(Service.update, record.id, record)),
    );

    yield all(recordsForDelete.map((record) => call(Service.delete, record.id)));

    const resultMarkers: IpWhitelistRecord[] = [...recordsNonChanged, ...updated, ...created];

    yield put(actions.changeIpWhitelistRecord.success(resultMarkers));
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.changeIpWhitelistRecord.failure(error));
  }
}

function* watchChangeIpWhitelistRecords() {
  yield takeEvery(actions.changeIpWhitelistRecord.request, changeFhqIpWhitelistRecordAsync);
}

// all
function* watchFhqIpWhitelistRecords(): SagaGenerator {
  yield all([watchFetchIpWhitelistRecords(), watchChangeIpWhitelistRecords()]);
}

export default watchFhqIpWhitelistRecords;
